import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import { canonicalMoney, sumMoney, multiplyByPercent, addMoney, formatMoney, multiplyMoney } from '../../lib/money';
import { createInvoice } from '../../lib/invoiceMutations';
import { useCreateScheduleEntry } from '../../hooks/useCreateScheduleEntry';
import { log as logger } from '../../lib/logger';
import { useAuthStore } from '../../stores/authStore';
import { localWallClockToInstant } from '../../components/schedule/scheduleHelpers';
import type { WorkOrder, WorkOrderItem } from '../../types';
import type { SortDirection } from '../../hooks/useSortState';

async function log(biz: string, type: string, desc: string, eId: string, uid: string) {
  try { await supabase.from('activity_log').insert({ business_id: biz, activity_type: type, description: desc, entity_type: 'work_order', entity_id: eId, logged_by: uid }); } catch { /* */ }
}

export interface JobRow extends WorkOrder {
  assigned_name?: string;
}

export interface JobFilters {
  status: string | null;
  priority: string | null;
  assignedId: string | null;
  search: string;
}

export interface JobSummary {
  activeCount: number;
  thisWeek: number;
  overdueCount: number;
  revenueThisMonth: number;
}

interface TeamUser { id: string; first_name: string; last_name: string; }

interface UseJobsOptions {
  from: number;
  to: number;
  sortColumn: string | null;
  sortDirection: SortDirection;
}

export function useJobs(businessId: string | undefined, options: UseJobsOptions) {
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [completedJobs, setCompletedJobs] = useState<JobRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<JobFilters>({ status: null, priority: null, assignedId: null, search: '' });
  const [summary, setSummary] = useState<JobSummary>({ activeCount: 0, thisWeek: 0, overdueCount: 0, revenueThisMonth: 0 });
  const mountedRef = useRef(true);
  const createScheduleEntry = useCreateScheduleEntry();

  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      // Build paginated + filtered query
      let query = supabase.from('work_orders').select('*', { count: 'exact' }).eq('business_id', businessId);
      if (filters.status) query = query.eq('status', filters.status);
      if (filters.priority) query = query.eq('priority', filters.priority);
      if (filters.assignedId) query = query.eq('assigned_id', filters.assignedId);
      if (debouncedSearch) {
        const q = `%${debouncedSearch}%`;
        query = query.or(`title.ilike.${q},client_name.ilike.${q}`);
      }
      const sortCol = options.sortColumn ?? 'created_at';
      const sortAsc = options.sortColumn ? options.sortDirection === 'asc' : false;
      query = query.order(sortCol, { ascending: sortAsc }).range(options.from, options.to);

      // Summary count queries
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
      const monthStart = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
      const activeStatuses = ['new', 'assigned', 'en_route', 'on_site'];

      const [jobsRes, usersRes, activeRes, completedWeekRes, overdueRes, revenueRes, completedRes] = await Promise.all([
        query.abortSignal(ctrl.signal),
        supabase.from('users').select('id, first_name, last_name').eq('business_id', businessId)
          .eq('status', 'active').abortSignal(ctrl.signal),
        supabase.from('work_orders').select('*', { count: 'exact', head: true }).eq('business_id', businessId)
          .in('status', activeStatuses).abortSignal(ctrl.signal),
        supabase.from('work_orders').select('*', { count: 'exact', head: true }).eq('business_id', businessId)
          .eq('status', 'completed').gte('completed_at', weekAgo).abortSignal(ctrl.signal),
        supabase.from('work_orders').select('*', { count: 'exact', head: true }).eq('business_id', businessId)
          .in('status', activeStatuses).not('scheduled_date', 'is', null).lt('scheduled_date', today).abortSignal(ctrl.signal),
        supabase.from('work_orders').select('total_amount').eq('business_id', businessId)
          .eq('status', 'completed').gte('completed_at', monthStart).abortSignal(ctrl.signal),
        // Non-paginated fetch for CompletedTab (completed + cancelled jobs)
        supabase.from('work_orders').select('*').eq('business_id', businessId)
          .in('status', ['completed', 'cancelled']).order('created_at', { ascending: false }).limit(200).abortSignal(ctrl.signal),
      ]);

      if (jobsRes.error) throw jobsRes.error;
      const uMap = new Map((usersRes.data ?? []).map(u => [u.id, `${u.first_name} ${u.last_name}`]));
      const rows = (jobsRes.data ?? []).map(j => ({ ...j, assigned_name: j.assigned_id ? uMap.get(j.assigned_id) ?? null : null })) as JobRow[];

      const revenueThisMonth = Number(sumMoney((revenueRes.data ?? []).map((j: { total_amount: number | null }) => canonicalMoney(j.total_amount ?? 0))));

      const completedRows = (completedRes.data ?? []).map(j => ({ ...j, assigned_name: j.assigned_id ? uMap.get(j.assigned_id) ?? null : null })) as JobRow[];

      // Reverse-lookup linked invoices for both lists. invoices.work_order_id
      // is the canonical FK; work_orders.invoice_id does not exist in live DB.
      // One batched query feeds both rows and completedRows.
      const allJobIds = Array.from(new Set([...rows.map(r => r.id), ...completedRows.map(r => r.id)]));
      if (allJobIds.length > 0) {
        const { data: invRows, error: invErr } = await supabase.from('invoices')
          .select('id, work_order_id').in('work_order_id', allJobIds)
          .abortSignal(ctrl.signal);
        if (invErr && !invErr.message.includes('abort')) logger.warn('Invoice reverse-lookup failed:', invErr.message);
        const invMap = new Map<string, string>();
        for (const row of (invRows ?? []) as { id: string; work_order_id: string | null }[]) {
          if (row.work_order_id) invMap.set(row.work_order_id, row.id);
        }
        for (const r of rows) r.invoice_id = invMap.get(r.id) ?? null;
        for (const r of completedRows) r.invoice_id = invMap.get(r.id) ?? null;
      }

      if (mountedRef.current) {
        setJobs(rows);
        setCompletedJobs(completedRows);
        setTotalCount(jobsRes.count ?? 0);
        setUsers((usersRes.data ?? []) as TeamUser[]);
        setSummary({
          activeCount: activeRes.count ?? 0,
          thisWeek: completedWeekRes.count ?? 0,
          overdueCount: overdueRes.count ?? 0,
          revenueThisMonth,
        });
      }
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId, options.from, options.to, options.sortColumn, options.sortDirection, filters.status, filters.priority, filters.assignedId, debouncedSearch]);

  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['work_orders'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  // CRUD
  const createJob = useCallback(async (data: Partial<WorkOrder>, userId: string): Promise<string | null> => {
    if (!businessId) return null;
    try {
      const { data: wo, error: e } = await supabase.from('work_orders').insert({
        ...data, business_id: businessId, status: 'new', created_by: userId,
      }).select('id').maybeSingle();
      if (e || !wo) return null;
      if (data.scheduled_date) {
        const st = data.scheduled_time || '09:00';
        const [h, m] = st.split(':').map(Number);
        // start_at/end_at are timestamptz — build true instants at the business tz
        // (A2 P2), not naive wall-clock-as-UTC strings.
        const tz = useAuthStore.getState().business?.timezone ?? null;
        const entryRes = await createScheduleEntry({
          type: 'job',
          title: data.title ?? 'Job',
          startAt: localWallClockToInstant(data.scheduled_date, `${st}:00`, tz),
          endAt: localWallClockToInstant(data.scheduled_date, `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`, tz),
          assignedTo: data.assigned_id ? [data.assigned_id] : undefined,
          jobId: wo.id,
          locationName: data.location || undefined,
          locationAddress: data.address || undefined,
          idempotencyKey: crypto.randomUUID(),
        });
        if (!entryRes.success) {
          logger.warn('Schedule entry creation failed for job:', wo.id, entryRes.error.code);
        }
      }
      await log(businessId, 'job_created', `Job created: ${data.title}`, wo.id, userId);
      void fetch(); return wo.id;
    } catch { return null; }
  }, [businessId, fetch, createScheduleEntry]);

  const updateStatus = useCallback(async (id: string, status: string, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const updates: Record<string, unknown> = { status };
      if (status === 'completed') updates.completed_at = new Date().toISOString();
      if (status === 'on_site') updates.started_at = new Date().toISOString();
      const { error: e } = await supabase.from('work_orders').update(updates).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, `job_${status}`, `Job ${status}`, id, userId);

      if (status === 'completed') {
        try {
          // Reverse lookup at line 4 below guards against duplicate auto-invoices
          // even before idempotencyKey gets a chance — invoice_dismissed flag also
          // suppresses re-creation if the operator manually dismissed once.
          const [{ data: job }, { data: items }, { data: existingInvoice }] = await Promise.all([
            supabase.from('work_orders').select('client_name, client_phone, invoice_dismissed').eq('id', id).maybeSingle(),
            supabase.from('work_order_items').select('description, quantity, unit_price, amount').eq('work_order_id', id),
            supabase.from('invoices').select('id').eq('work_order_id', id).limit(1).maybeSingle(),
          ]);
          if (job?.client_name && items && items.length > 0 && !existingInvoice && !job.invoice_dismissed) {
            const lineItems = items.map(li => ({ description: li.description, quantity: li.quantity, unit_price: li.unit_price, amount: li.amount }));
            // Rule #34: canonical sum drives the activity-log copy below; the
            // RPC recomputes subtotal/tax/total server-side from line_items.
            const subtotalCanonical = sumMoney(lineItems.map(li => canonicalMoney(li.amount)));
            const taxCanonical = multiplyByPercent(subtotalCanonical, 13);
            const totalCanonical = addMoney(subtotalCanonical, taxCanonical);
            const dueDate = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];

            // Phase B.3.2: routed through canonical RPC. Identity, business
            // snapshot, totals, invoice_number, project_id resolve server-side.
            // tax_rate is now stored as fraction (0.13) — earlier direct-insert
            // path stored 13 (percent), an inconsistency with standalone invoices.
            const result = await createInvoice({
              businessId,
              clientId: null,
              lineItems,
              dueDate,
              notes: 'Auto-generated from completed job',
              taxRate: 0.13,
              idempotencyKey: `job-completion-${id}`,
              clientName: job.client_name,
              clientPhone: job.client_phone || null,
              linkedWorkOrderId: id,
            });
            if (result.success) {
              await log(businessId, 'invoice_created', `Draft invoice auto-created — ${formatMoney(totalCanonical)}`, id, userId);
            }
          }
        } catch { /* non-critical */ }
      }

      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  const updateJob = useCallback(async (id: string, data: Partial<WorkOrder>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('work_orders').update(data).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'job_updated', `Job updated: ${data.title ?? id}`, id, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  // Multi-date batch — wired by CreateJobPanel when dateMode='multi'.
  // The panel calls onCreate first (creates a parent stub with NULL date),
  // then onCreateBatch with { ...baseData, job_id: parent.id } and the date list.
  // We delete the parent stub and insert N work_orders, one per picked date,
  // so the user gets exactly N dated rows (no orphan no-date stub).
  const createJobBatch = useCallback(async (
    data: Partial<WorkOrder> & { job_id?: string },
    dates: string[],
    userId: string,
  ): Promise<boolean> => {
    if (!businessId || dates.length === 0) return false;
    try {
      const { job_id: parentId, ...rest } = data as Partial<WorkOrder> & { job_id?: string };
      // Drop the parent stub created by the preceding onCreate call.
      if (parentId) {
        await supabase.from('work_orders').delete().eq('id', parentId).eq('business_id', businessId);
      }
      // Insert one work_order per picked date, plus its mirrored schedule_entry.
      const inserted: string[] = [];
      for (const d of dates) {
        const { data: wo } = await supabase.from('work_orders').insert({
          ...rest, business_id: businessId, status: 'new', created_by: userId,
          scheduled_date: d,
        }).select('id').maybeSingle();
        if (!wo) continue;
        inserted.push(wo.id);
        const st = (rest.scheduled_time as string | null) || '09:00';
        const [h, m] = st.split(':').map(Number);
        // start_at/end_at are timestamptz — build true instants at the business tz
        // (A2 P2), not naive wall-clock-as-UTC strings.
        const tz = useAuthStore.getState().business?.timezone ?? null;
        const entryRes = await createScheduleEntry({
          type: 'job',
          title: rest.title ?? 'Job',
          startAt: localWallClockToInstant(d, `${st}:00`, tz),
          endAt: localWallClockToInstant(d, `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`, tz),
          assignedTo: rest.assigned_id ? [rest.assigned_id] : undefined,
          jobId: wo.id,
          locationName: rest.location || undefined,
          locationAddress: rest.address || undefined,
          idempotencyKey: crypto.randomUUID(),
        });
        if (!entryRes.success) {
          logger.warn('Schedule entry creation failed for batch job:', wo.id, entryRes.error.code);
        }
      }
      await log(businessId, 'jobs_batch_created', `Created ${inserted.length} jobs across ${dates.length} dates: ${rest.title ?? 'Job'}`, inserted[0] ?? null, userId);
      void fetch();
      return inserted.length > 0;
    } catch { return false; }
  }, [businessId, fetch, createScheduleEntry]);

  return { jobs, completedJobs, totalCount, users, loading, error, summary, filters, setFilters, refresh: fetch, createJob, createJobBatch, updateStatus, updateJob };
}

// ── Line items ──
export function useJobItems(jobId: string | null) {
  const [items, setItems] = useState<WorkOrderItem[]>([]);
  const fetch = useCallback(async () => {
    if (!jobId) { setItems([]); return; }
    const { data } = await supabase.from('work_order_items').select('*').eq('work_order_id', jobId).order('created_at');
    setItems((data ?? []) as WorkOrderItem[]);
  }, [jobId]);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void fetch(); }, [fetch]);

  const addItem = useCallback(async (item: Partial<WorkOrderItem>, bizId: string, userId: string): Promise<boolean> => {
    if (!jobId) return false;
    // Rule #34: qty × unit_price through the Money wrapper.
    const amount = Number(multiplyMoney(canonicalMoney(item.unit_price ?? 0), item.quantity ?? 1));
    const { error: e } = await supabase.from('work_order_items').insert({ ...item, work_order_id: jobId, business_id: bizId, amount, added_by: userId });
    if (e) return false;
    await log(bizId, 'line_item_added', `Line item: ${item.description}`, jobId, userId);
    void fetch(); return true;
  }, [jobId, fetch]);

  const removeItem = useCallback(async (itemId: string, bizId: string, userId: string): Promise<boolean> => {
    if (!jobId) return false;
    const { error: e } = await supabase.from('work_order_items').delete().eq('id', itemId);
    if (e) return false;
    await log(bizId, 'line_item_removed', `Line item removed`, jobId, userId);
    void fetch(); return true;
  }, [jobId, fetch]);

  const total = useMemo(() => Number(sumMoney(items.map(i => canonicalMoney(i.amount)))), [items]);
  return { items, total, addItem, removeItem, refresh: fetch };
}
